// src/app/survey01/page.tsx
//
// 業種横断「続編アンケート」。サインアップ不要で回答可。
// ページ本体・質問文はCEOスレッドで確定済み（TASKS_ACTIVE.md参照、Kazuto承認済み・直し不要）。
// 2026-07-26・URL命名：当初/school-interestだったが「業種横断なのにURLにschoolが残るのは
// 誤解を招く」とのKazuto指摘で改名。今後の軽量フォームはsurvey01, survey02...と連番で
// 命名する方針（IDEAS.md「軽量フォーム乱立とCRM不在という構造的な弱さ」参照）。
"use client";

import { useState, useEffect } from "react";
import { useUser } from "@clerk/nextjs";

// Q1: /survey の occupation 選択肢をそのまま流用（src/app/survey/page.tsx から転記）
const OCCUPATION_OPTIONS = [
  { key: "education",   label: "教育関係者" },
  { key: "facilitator", label: "ファシリテーター" },
  { key: "trainer",     label: "研修講師・人材開発担当" },
  { key: "coach",       label: "コーチ・カウンセラー" },
  { key: "medical",     label: "医療・福祉関係者" },
  { key: "executive",   label: "経営者・管理職" },
  { key: "creative",    label: "クリエイター・デザイナー" },
  { key: "consultant",  label: "コンサルタント・アドバイザー" },
  { key: "personal",    label: "個人（仕事以外での活用）" },
  { key: "other",       label: "その他" },
] as const;

const POSSIBILITY_OPTIONS = [
  { key: "support_writing",      label: "書けない人を後押しする側" },
  { key: "escape_safe_answers",  label: "無難な答えから抜け出す側" },
  { key: "both",                 label: "両方" },
  { key: "unsure",               label: "まだ分からない" },
] as const;

const ENGAGEMENT_OPTIONS = [
  { key: "early_monitor",       label: "実際に試してみたい＝先行モニター" },
  { key: "feedback_provider",   label: "意見や現場の声を伝えたい" },
  { key: "framework_provider",  label: "自分の型・切り口を提供する側として関わってみたい" },
  { key: "notify_when_ready",   label: "完成したら知りたい" },
] as const;

const ANNUAL_INTEREST_OPTIONS = [
  { key: "very_interested", label: "ぜひ検討したい" },
  { key: "depends",         label: "条件次第" },
  { key: "not_now",         label: "今は考えていない" },
] as const;

const STORAGE_KEY = "kataruta_survey01_submitted";

export default function SchoolInterestPage() {
  const { user, isLoaded } = useUser();
  const [step, setStep] = useState<"form" | "done">("form");

  const [occupation, setOccupation] = useState("");
  const [occupationFreeText, setOccupationFreeText] = useState("");
  const [possibility, setPossibility] = useState("");
  const [useCaseFreeText, setUseCaseFreeText] = useState("");
  const [engagement, setEngagement] = useState("");
  const [annualInterest, setAnnualInterest] = useState("");
  const [email, setEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") setStep("done");
    } catch {}
  }, []);

  const isSignedIn = isLoaded && !!user;

  const canSubmit =
    occupation.trim() !== "" &&
    possibility.trim() !== "" &&
    engagement.trim() !== "" &&
    annualInterest.trim() !== "" &&
    (isSignedIn || email.trim() !== "");

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch("/api/survey01", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          occupation,
          occupationFreeText: occupation === "other" ? occupationFreeText : undefined,
          possibility,
          useCaseFreeText: useCaseFreeText.trim() ? useCaseFreeText : undefined,
          engagement,
          annualInterest,
          // ログイン中はサーバー側でuid/emailを自動付与するため送らない（未ログイン時のみ使用される）
          email: isSignedIn ? undefined : email,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setSubmitError(data?.error ?? "送信に失敗しました。もう一度お試しください。");
        return;
      }

      setStep("done");
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    } catch {
      setSubmitError("通信エラーが発生しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  // ── サンクス表示 ──────────────────────────────────────────────────────────

  if (step === "done") {
    return (
      <main className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-xl px-4 py-12">
          <p className="text-xs tracking-widest text-gray-400 uppercase mb-8 text-center">
            KATARUTA ONLINE
          </p>
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <h1 className="text-xl font-bold text-gray-900 mb-4">
              ご協力ありがとうございました。
            </h1>
            <p className="text-sm text-gray-600 leading-relaxed">
              いただいたお声は、「現場別の使い方」づくりに活かしていきます。先行してご意見をくださった方には、形になった際に一番にお伝えします。
            </p>
          </div>
        </div>
      </main>
    );
  }

  // ── フォーム表示 ─────────────────────────────────────────────────────────

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-xl px-4 py-12">
        <p className="text-xs tracking-widest text-gray-400 uppercase mb-8 text-center">
          KATARUTA ONLINE
        </p>

        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">「書けなさ」のデザイン</h1>
          <div className="mt-4 space-y-3 text-sm text-gray-600 leading-relaxed">
            <p>カタルタオンラインでは今、新しい回答用紙のようなものを考えています。</p>
            <p>
              最初は無茶振りに感じるのに、いざ書いてみると意外な言葉が出てきて自分でも驚く。そんな「書けなさ」が面白さを生む側面と、カードの言葉が手掛かりになって書きやすくなる側面、その両方を行き来できる仕組みを作れないか、というのが今考えていることです。
            </p>
            <p>これがどんな現場で役立ちそうか、少し聞かせてください。3分ほどで終わります。</p>
          </div>
        </div>

        {/* Q1 */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">Q1. お仕事・活動の分野</p>
          <div className="space-y-2">
            {OCCUPATION_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="occupation"
                  value={opt.key}
                  checked={occupation === opt.key}
                  onChange={() => { setOccupation(opt.key); setOccupationFreeText(""); }}
                  className="w-4 h-4 accent-black flex-shrink-0"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
          {occupation === "other" && (
            <div className="mt-4 pl-4 border-l-2 border-gray-100">
              <input
                type="text"
                placeholder="分野を入力（任意）"
                value={occupationFreeText}
                onChange={(e) => setOccupationFreeText(e.target.value)}
                maxLength={200}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-400"
              />
            </div>
          )}
        </div>

        {/* Q2 */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            Q2. 今の話を読んで、どちらの側面に可能性を感じますか
          </p>
          <div className="space-y-2">
            {POSSIBILITY_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="possibility"
                  value={opt.key}
                  checked={possibility === opt.key}
                  onChange={() => setPossibility(opt.key)}
                  className="w-4 h-4 accent-black flex-shrink-0"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 自由記述（2026-07-26・CEO室依頼：ブログ文面で約束していた自由記述欄が
            設問に無かった不備への対応。Q2とQ3の間・任意・スキップ可） */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            どんな場面で使えそうか、よければ教えてください
            <span className="text-xs font-normal text-gray-400 ml-1">任意</span>
          </p>
          <textarea
            value={useCaseFreeText}
            onChange={(e) => setUseCaseFreeText(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="例：感想が「楽しかったです」止まり／振り返りがいつも同じ言葉、など"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-400"
          />
        </div>

        {/* Q3 */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            Q3. この取り組みに、どのくらい関わってみたいですか
          </p>
          <div className="space-y-2">
            {ENGAGEMENT_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="engagement"
                  value={opt.key}
                  checked={engagement === opt.key}
                  onChange={() => setEngagement(opt.key)}
                  className="w-4 h-4 accent-black flex-shrink-0"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Q4 */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            Q4. もし年間契約のような形で導入できるとしたら、興味はありますか
          </p>
          <div className="space-y-2">
            {ANNUAL_INTEREST_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="annualInterest"
                  value={opt.key}
                  checked={annualInterest === opt.key}
                  onChange={() => setAnnualInterest(opt.key)}
                  className="w-4 h-4 accent-black flex-shrink-0"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Q5：ログイン中は非表示・自動保存、未ログインは必須 */}
        {isLoaded && !isSignedIn && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <p className="text-sm font-semibold text-gray-800 mb-3">Q5. メールアドレス</p>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-400"
            />
          </div>
        )}

        {submitError && (
          <p className="text-sm text-red-500 text-center mb-4">{submitError}</p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="w-full bg-black text-white text-sm font-semibold py-3 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40"
        >
          {submitting ? "送信中..." : "回答する"}
        </button>

        <p className="mt-4 text-xs text-center text-gray-400">
          {isSignedIn ? "ログイン中のアカウントのメールアドレスで送信されます" : "回答はアカウントなしで送信できます"}
        </p>
      </div>
    </main>
  );
}
